import { computed, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, combineLatestWith, defer, map, Observable, tap } from 'rxjs';
import { ViewFilterShareTargetDto } from '@soff/shared-ui/filter-bar';
import { SoffViewFilterContext } from '@soff/shared-ui/saved-views';
import { EmployeeGroupSto, EmployeeSto } from './features/employees/models/employee.model.sto';
import { EmployeeActions, EmployeeSelectors } from './features/employees/state';
import { AccountSelectors } from './shared/state/account';

/**
 * SOFF adapter for `SOFF_VIEW_FILTER_CONTEXT`: current user from the account store, share targets
 * (employees, own workshops, employee groups) from the employees store. Must run in an injection context.
 */
export function soffViewFilterContextFactory(): SoffViewFilterContext {
  const store = inject(Store);
  const userProfile = store.selectSignal(AccountSelectors.getUserProfile);

  const loadShareTargets = (): Observable<ViewFilterShareTargetDto[]> =>
    defer(() => {
      store.dispatch(new EmployeeActions.LoadEmployeesPage({ limit: 100, offset: 0, autoloadNextPage: true }));

      const employees$ = store.select(EmployeeSelectors.getEmployeesList).pipe(
        map((employees: EmployeeSto[]) =>
          (employees || []).map(
            employee =>
              ({
                aggregateId: employee.aggregateId,
                aggregateType: 'Employee',
                name: `${employee.firstname} ${employee.lastname}`,
              }) as ViewFilterShareTargetDto
          )
        )
      );

      const workshopsAndGroups$ = store.select(AccountSelectors.getUserWorkshops).pipe(
        tap(workshops =>
          workshops?.forEach(workshop =>
            store.dispatch(
              new EmployeeActions.LoadEmployeeGroups({
                workshopId: workshop.aggregateId,
                limit: 100,
                offset: 0,
                force: false,
                autoloadNextPage: true,
              })
            )
          )
        ),
        combineLatestWith(store.select(EmployeeSelectors.getEmployeeGroups)),
        map(([workshops, employeeGroups]) => [
          ...(workshops?.map(
            workshop =>
              ({
                aggregateId: workshop.aggregateId,
                aggregateType: 'Workshop',
                name: `${workshop.aggregateId} - ${workshop.name} ${workshop.name2 || ''}`,
              }) as ViewFilterShareTargetDto
          ) || []),
          ...[Object.values(employeeGroups || {}) as EmployeeGroupSto[][]].flat(2).map(
            employeeGroup =>
              ({
                aggregateId: employeeGroup.aggregateId,
                aggregateType: 'EmployeeGroup',
                name: employeeGroup.name,
              }) as ViewFilterShareTargetDto
          ),
        ])
      );

      return combineLatest([employees$, workshopsAndGroups$]).pipe(map(([employees, others]) => [...employees, ...others]));
    });

  return {
    currentUserId: computed(() => userProfile()?.userId?.toLowerCase() ?? null),
    loadShareTargets,
  };
}
